import { html, ref, when, repeat } from '@microsoft/fast-element';
import { sync } from '@genesislcap/foundation-utils';
import type { GeneralTab } from './general-tab';
import { roundNumber } from '../../../../../utils/formatting';
import { PROJECT_OPERATIONAL_STATUS, TECHNOLOGY } from '../../../../../utils';

export const GeneralTabTemplate = html<GeneralTab>`
  <form ${ref('generalForm')}>
    <zero-text-field
      ${ref('projectName')}
      id="projectName"
      :value=${sync((x) => x.projectNameValue)}
      class="input-control"
      type="text"
      placeholder="Enter..."
      maxlength="64"
      required
    >
      Project Name
    </zero-text-field>

    <div class="form-control">
      <label>Project Legal Name</label>
      <zero-select
        ${ref('legalName')}
        id="legalName"
        :value=${sync((x) => x.legalNameValue)}
        class="input-control"
        :disabled="${(x) => x.projectModalService.modalMode === 'edit'}"
      >
        ${repeat(
          (x) => x.companies,
          html`
            <zero-option value="${(x) => x.NAME}">${(x) => x.NAME}</zero-option>
          `,
        )}
      </zero-select>
    </div>

    <div class="form-control">
      <label class="form-label">Project Operational Status</label>
      <zero-segmented-control
        ${ref('techType')}
        id="techType"
        appearance="primary"
        orientation="horizontal"
        ?disabled="${(x) => x.isOperational}"
        :value="${sync((x) => x.projectOperationalStatusValue)}"
      >
        <zero-segmented-item value=${PROJECT_OPERATIONAL_STATUS.OPERATIONAL}>
          Operational
        </zero-segmented-item>
        <zero-segmented-item value=${PROJECT_OPERATIONAL_STATUS.NEW_BUILD}>
          New Build
        </zero-segmented-item>
      </zero-segmented-control>
    </div>

    <div class="form-control">
      <label>Owner</label>
      <zero-select
        ${ref('owner')}
        id="owner"
        :value=${sync((x) => x.ownerValue)}
        class="input-control"
        disabled
      >
        <zero-option value="${(x) => x.companyService.getCurrentCompany().NAME}">
          ${(x) => x.companyService.getCurrentCompany().NAME}
        </zero-option>
      </zero-select>
    </div>

    <zero-text-field
      ${ref('operator')}
      id="operator"
      :value=${sync((x) => x.operatorValue)}
      class="input-control"
      type="text"
      placeholder="Enter..."
      required
    >
      Operator
    </zero-text-field>

    <div class="form-control">
      <label class="form-label">Technology Type</label>
      <zero-segmented-control
        :disabled="${(x) => x.projectModalService.modalMode === 'edit'}"
        ${ref('techType')}
        id="techType"
        appearance="primary"
        orientation="horizontal"
        :value="${sync((x) => x.techTypeValue)}"
      >
        <zero-segmented-item @click=${(x) => x.technologyChanged(true)} value="WIND">
          Wind
        </zero-segmented-item>
        <zero-segmented-item @click=${(x) => x.technologyChanged(false)} value="SOLAR">
          Solar
        </zero-segmented-item>
      </zero-segmented-control>
    </div>

    ${when(
      (e) => e.techTypeValue === TECHNOLOGY.WIND,
      html`
        <div class="wind-section">
          <zero-number-field
            :value="${sync((x) => x.windCapacityMWACValue)}"
            class="input-control"
            id="nameplateCapacityWind"
            min="0"
            placeholder="Enter..."
            @blur=${(x) =>
              x.blurRoundNumber(
                x.windCapacityMWAC,
                x.windCapacityMWACValue,
                'windCapacityMWACValue',
              )}
            hide-step
            withFormatting
            required="${(x) => x.techTypeValue === TECHNOLOGY.WIND}"
            ${ref('windCapacityMWAC')}
          >
            ${(x) => x.getNameplateLabel(x.techTypeValue, 'AC')}
            <info-icon id="nameplateCapacityWindTooltip"></info-icon>
          </zero-number-field>
          <zero-tooltip anchor="nameplateCapacityWindTooltip" position="top">
            The maximum rated output of the energy generation facility, expressed in megawatts AC
            (MW
            <sub>AC</sub>
            )
          </zero-tooltip>
          <zero-number-field
            ${ref('windAvailableCapacityMWAC')}
            :value=${sync((x) => x.availableCapacityMWACValue)}
            class="input-control"
            id="availableCapacityWind"
            min="0"
            hide-step
            withFormatting
            @blur=${(x) =>
              x.blurRoundNumber(
                x.windAvailableCapacityMWAC,
                x.availableCapacityMWACValue,
                'availableCapacityMWACValue',
              )}
            placeholder="Enter..."
            required=${(x) => x.techTypeValue === TECHNOLOGY.WIND}
          >
            Available Capacity (MW
            <sub>AC</sub>
            )
            <info-icon id="availableCapacityWindTooltip"></info-icon>
          </zero-number-field>
          <zero-tooltip anchor="availableCapacityWind" position="top">
            The maximum rated output of the portion of the generator that the seller is willing or
            able to contract, expressed in megawatts AC (MW
            <sub>AC</sub>
            )
          </zero-tooltip>
          <zero-text-field
            ${ref('turbineMakeModel')}
            :value=${sync((x) => x.turbineMakeModelValue)}
            class="input-control"
            type="text"
            placeholder="Enter..."
            withFormatting
            required=${(x) => x.techTypeValue === TECHNOLOGY.WIND}
          >
            Turbine Make and Model
          </zero-text-field>
          <zero-text-field
            ${ref('turbineCount')}
            :value=${sync((x) => x.turbineCountValue)}
            class="input-control"
            type="number"
            min="0"
            oninput="this.value = !!this.value && Math.abs(this.value) >= 0 ? Math.abs(this.value) : null"
            placeholder="Enter..."
            required=${(x) => x.techTypeValue === TECHNOLOGY.WIND}
          >
            Turbine Count
          </zero-text-field>
          <zero-number-field
            ${ref('hubHeight')}
            :value=${sync((x) => x.hubHeightValue)}
            class="input-control"
            min="10.01"
            max="299.99"
            hide-step
            @blur=${(x) =>
              (x.hubHeightValue = roundNumber(x.hubHeightValue)
                ? roundNumber(x.hubHeightValue).toString()
                : '')}
            placeholder="Enter..."
            required=${(x) => x.techTypeValue === TECHNOLOGY.WIND}
          >
            Hub Height (m)
          </zero-number-field>
          <zero-number-field
            ${ref('turbineNameplate')}
            :value=${sync((x) => x.turbineNameplateValue)}
            class="input-control"
            min="0"
            hide-step
            withFormatting
            @blur=${(x) =>
              x.blurRoundNumber(
                x.turbineNameplate,
                x.turbineNameplateValue,
                'turbineNameplateValue',
              )}
            placeholder="Enter..."
            required=${(x) => x.techTypeValue === TECHNOLOGY.WIND}
          >
            Turbine Nameplate (MW)
          </zero-number-field>
          <zero-number-field
            ${ref('turbineDiameter')}
            :value=${sync((x) => x.turbineDiameterValue)}
            class="input-control"
            min="6"
            max="299"
            hide-step
            @blur=${(x) =>
              (x.turbineDiameterValue = roundNumber(x.turbineDiameterValue, 0)
                ? roundNumber(x.turbineDiameterValue, 0).toString()
                : '')}
            placeholder="Enter..."
            required=${(x) => x.techTypeValue === TECHNOLOGY.WIND}
          >
            Turbine Diameter (m)
          </zero-number-field>
        </div>
      `,
    )}
    ${when(
      (x) => x.techTypeValue === TECHNOLOGY.SOLAR,
      html`
        <div class="solar-section">
          <div class="form-control long-error">
            <zero-number-field
              :value=${sync((x) => x.solarCapacityMWDCValue)}
              class="input-control"
              min="0"
              hide-step
              withFormatting
              placeholder="Enter..."
              required=${(x) => x.techTypeValue === TECHNOLOGY.SOLAR}
              turbineDiameter
              @blur=${(x) =>
                x.blurRoundNumber(
                  x.solarCapacityMWDC,
                  x.solarCapacityMWDCValue,
                  'solarCapacityMWDCValue',
                )}
              ${ref('solarCapacityMWDC')}
              id="solarCapacityMWDC"
            >
              ${(x) => x.getNameplateLabel(x.techTypeValue, 'DC')}
            </zero-number-field>
          </div>
          <zero-number-field
            :value=${sync((x) => x.solarCapacityMWACValue)}
            class="input-control"
            min="0"
            hide-step
            withFormatting
            @blur=${(x) =>
              x.blurRoundNumber(
                x.solarCapacityMWAC,
                x.solarCapacityMWACValue,
                'solarCapacityMWACValue',
              )}
            placeholder="Enter..."
            required=${(x) => x.techTypeValue === TECHNOLOGY.SOLAR}
            ${ref('solarCapacityMWAC')}
            id="solarCapacityMWAC"
          >
            ${(x) => x.getNameplateLabel(x.techTypeValue, 'AC')}
            <info-icon id="nameplateCapacitySolarTooltip"></info-icon>
          </zero-number-field>
          <zero-tooltip anchor="nameplateCapacitySolarTooltip" position="top">
            The maximum rated output of the energy generation facility, expressed in megawatts AC
            (MW
            <sub>AC</sub>
            )
          </zero-tooltip>
          <zero-number-field
            ${ref('solarAvailableCapacityMWAC')}
            :value=${sync((x) => x.solarAvailableCapacityMWACValue)}
            class="input-control"
            id="availableCapacitySolar"
            min="0"
            hide-step
            withFormatting
            @blur=${(x) =>
              x.blurRoundNumber(
                x.solarAvailableCapacityMWAC,
                x.solarAvailableCapacityMWACValue,
                'solarAvailableCapacityMWACValue',
              )}
            placeholder="Enter..."
            required=${(x) => x.techTypeValue === TECHNOLOGY.SOLAR}
            id="availableCapacitySolar"
          >
            Available Capacity (AC)
            <info-icon id="availableCapacitySolarTooltip"></info-icon>
          </zero-number-field>
          <zero-tooltip anchor="availableCapacitySolarTooltip" position="top">
            The maximum rated output of the portion of the generator that the seller is willing or
            able to contract, expressed in megawatts AC (MW
            <sub>AC</sub>
            )
          </zero-tooltip>
          <div class="form-control required">
            <label class="form-label">Panel Type</label>
            <zero-segmented-control
              ${ref('panelType')}
              appearance="primary"
              :value=${sync((x) => x.panelTypeValue)}
              class="input-control"
              id="panelType"
            >
              <zero-segmented-item @click=${(x) => x.panelTypeChanged(false)} value="MONO">
                Mono
              </zero-segmented-item>
              <zero-segmented-item @click=${(x) => x.panelTypeChanged(true)} value="BIFACIAL">
                Bifacial
              </zero-segmented-item>
            </zero-segmented-control>
          </div>
          <div class="form-control">
            <label>Panel Technology</label>
            <zero-select
              ${ref('panelTechnology')}
              id="panelTechnology"
              :value=${sync((x) => x.panelTechnologyValue)}
              class="input-control"
              ?disabled="${(x) => x.panelTypeValue === 'BIFACIAL'}"
            >
              ${repeat(
                (x) => x.panelTechnologyEnum,
                html`
                  <zero-option value="${(x) => x.ENUM_VALUE}">
                    ${(x) => x.READABLE_VALUE}
                  </zero-option>
                `,
              )}
            </zero-select>
            <zero-text-field
              ${ref('panelMakeModel')}
              :value=${sync((x) => x.panelMakeModelValue)}
              class="input-control"
              type="text"
              placeholder="Enter..."
              required=${(x) => x.techTypeValue === TECHNOLOGY.SOLAR}
              id="panelMakeModel"
            >
              Panel Make and Model
            </zero-text-field>
          </div>
          <div class="form-control">
            <label>Tracking Type</label>
            <zero-select
              ${ref('trackingType')}
              id="trackingType"
              :value=${sync((x) => x.trackingTypeValue)}
              class="input-control"
            >
              ${repeat(
                (x) => x.trackingTypeEnum,
                html`
                  <zero-option value="${(x) => x.ENUM_VALUE}">
                    ${(x) => x.READABLE_VALUE}
                  </zero-option>
                `,
              )}
            </zero-select>
          </div>
        </div>
      `,
    )}

    <zero-number-field
      ${ref('capacityFactor')}
      :value=${sync((x) => x.capacityFactorValue)}
      class="input-control"
      min="0"
      max="100"
      hide-step
      placeholder="Enter..."
      @blur=${(x) =>
        (x.capacityFactorValue = !!x.capacityFactorValue
          ? roundNumber(x.capacityFactorValue).toString()
          : '')}
    >
      P50 Capacity Factor (%)
    </zero-number-field>
  </form>
`;
